/* GestionTurnos.jsx  v9.0
   Modulo de Gestion de Turnos de Tecnicos
   Asignacion individual y en bloque con turnos predefinidos + personalizados */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Clock, Users, CalendarDays, Search, Filter, ChevronDown,
  CheckSquare, Square, Loader2, Trash2, Plus, X, Building2, MapPin,
} from "lucide-react";
import api from "../lib/api";
import { useGlobalFilters } from "../lib/globalFilters";

const PRESETS = {
  manana: { name: "Manana", start: "07:00", end: "15:00", color: "#F59E0B" },
  tarde: { name: "Tarde", start: "15:00", end: "23:00", color: "#3B82F6" },
  noche: { name: "Noche", start: "23:00", end: "07:00", color: "#7C3AED" },
};

export default function GestionTurnos() {
  const { filters, setFilter } = useGlobalFilters();
  const branchId = filters.branch_office_id || "";
  const zoneId = filters.zone_id || "";

  const [techs, setTechs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  // Shift form
  const [shiftDate, setShiftDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [shiftType, setShiftType] = useState("manana");
  const [startTime, setStartTime] = useState("07:00");
  const [endTime, setEndTime] = useState("15:00");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Existing shifts
  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);

  useEffect(() => {
    fetchTechs();
    fetchBranches();
  }, []);

  useEffect(() => { fetchShifts(); }, [shiftDate]);

  const fetchTechs = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/users/technicians");
      setTechs(r.data.items || r.data || []);
    } catch { toast.error("Error al cargar tecnicos"); }
    finally { setLoading(false); }
  };

  const fetchBranches = async () => {
    try {
      const r = await api.get("/v1/config/branch-offices");
      setBranches(r.data.items || r.data || []);
    } catch {}
  };

  const fetchShifts = async () => {
    setShiftsLoading(true);
    try {
      const r = await api.get("/v1/shifts", { params: { shift_date: shiftDate } });
      setShifts(r.data.items || []);
    } catch {}
    finally { setShiftsLoading(false); }
  };

  const filteredTechs = useMemo(() => {
    let list = techs;
    if (branchId) list = list.filter((t) => String(t.branch_id || t.branch_office_id || "") === String(branchId));
    if (zoneId) list = list.filter((t) => {
      // zone_ids is an array, zone_id is a single value
      const zids = t.zone_ids || [];
      return String(t.zone_id || "") === String(zoneId) || zids.includes(Number(zoneId)) || zids.includes(String(zoneId));
    });
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.email || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [techs, branchId, zoneId, search]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filteredTechs.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredTechs.map((t) => String(t.id))));
    }
  };

  const handlePresetChange = (preset) => {
    setShiftType(preset);
    if (PRESETS[preset]) {
      setStartTime(PRESETS[preset].start);
      setEndTime(PRESETS[preset].end);
    }
  };

  const handleAssign = async () => {
    if (selected.size === 0) {
      toast.error("Selecciona al menos un tecnico");
      return;
    }
    if (!startTime || !endTime) {
      toast.error("Define hora de inicio y fin");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/v1/shifts/bulk", {
        technician_ids: Array.from(selected),
        shift_date: shiftDate,
        shift_type: shiftType,
        start_time: startTime,
        end_time: endTime,
        notes,
        branch_office_id: branchId ? parseInt(branchId) : null,
        zone_id: zoneId ? parseInt(zoneId) : null,
      });
      toast.success(`${r.data.created} turnos asignados`);
      setSelected(new Set());
      setNotes("");
      fetchShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al asignar turnos");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShift = async (id) => {
    try {
      await api.delete(`/v1/shifts/${id}`);
      toast.success("Turno eliminado");
      fetchShifts();
    } catch {
      toast.error("Error al eliminar turno");
    }
  };

  // Zones from selected branch
  const branchZones = useMemo(() => {
    const b = branches.find((b) => String(b.id) === String(branchId));
    return b?.zones || [];
  }, [branches, branchId]);

  return (
    <div className="min-h-screen" style={{ background: "#E4EBF0" }} data-testid="gestion-turnos-page">
      {/* Header */}
      <div
        className="px-6 py-4 flex items-center gap-4 flex-wrap"
        style={{ background: "#0A3747" }}
      >
        <div className="flex items-center gap-2">
          <Clock size={22} className="text-[#E0E621]" />
          <h1 className="text-lg font-bold text-white">Gestion de Turnos</h1>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <select
            value={branchId}
            onChange={(e) => { setFilter("branch_office_id", e.target.value); setFilter("zone_id", ""); }}
            className="text-[11px] bg-white/10 text-white border border-white/20 rounded px-2.5 py-1.5 outline-none"
            data-testid="turnos-branch-filter"
          >
            <option value="" style={{ color: "#000" }}>Todas las sucursales</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id} style={{ color: "#000" }}>{b.name}</option>
            ))}
          </select>
          <select
            value={zoneId}
            onChange={(e) => setFilter("zone_id", e.target.value)}
            className="text-[11px] bg-white/10 text-white border border-white/20 rounded px-2.5 py-1.5 outline-none"
            data-testid="turnos-zone-filter"
          >
            <option value="" style={{ color: "#000" }}>Todas las zonas</option>
            {branchZones.map((z) => (
              <option key={z.id} value={z.id} style={{ color: "#000" }}>{z.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-4 flex gap-4 flex-col lg:flex-row">
        {/* Left: Technician selection */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm overflow-hidden" style={{ border: "1.5px solid #D3DBE4" }}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
            <Users size={16} className="text-[#0A3747]" />
            <span className="text-sm font-bold text-[#0A3747]">Tecnicos</span>
            <span className="text-[10px] px-2 py-0.5 rounded-lg font-mono font-bold bg-sky-50 text-sky-700">
              {filteredTechs.length}
            </span>
            {selected.size > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-lg font-bold bg-green-50 text-green-700">
                {selected.size} seleccionados
              </span>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <div className="flex items-center gap-1 bg-gray-50 rounded-lg px-2 py-1">
                <Search size={12} className="text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar tecnico..."
                  className="text-[11px] bg-transparent w-32 outline-none text-gray-700"
                  data-testid="turnos-search-tech"
                />
              </div>
              <button
                onClick={selectAll}
                className="text-[10px] font-semibold px-2.5 py-1 rounded-lg hover:bg-gray-50 transition"
                style={{ color: "#0A3747" }}
                data-testid="turnos-select-all"
              >
                {selected.size === filteredTechs.length && filteredTechs.length > 0 ? "Deseleccionar todo" : "Seleccionar todo"}
              </button>
            </div>
          </div>

          <div className="max-h-[520px] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 size={20} className="animate-spin text-gray-400" />
              </div>
            ) : filteredTechs.length === 0 ? (
              <div className="text-center text-sm text-gray-400 py-12">Sin tecnicos</div>
            ) : (
              filteredTechs.map((t) => {
                const tid = String(t.id);
                const isSelected = selected.has(tid);
                const assignedShift = shifts.find((s) => s.technician_id === tid);
                return (
                  <button
                    key={tid}
                    onClick={() => toggleSelect(tid)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 hover:bg-sky-50/40 transition text-left"
                    style={{ background: isSelected ? "#EFF6FF" : "transparent" }}
                    data-testid={`tech-row-${tid}`}
                  >
                    {isSelected ? (
                      <CheckSquare size={16} className="text-sky-600 flex-shrink-0" />
                    ) : (
                      <Square size={16} className="text-gray-300 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold text-[#0A3747] truncate">{t.name || t.email}</div>
                      <div className="text-[9px] text-gray-400 truncate">{t.email}</div>
                    </div>
                    {t.branch_name && (
                      <span className="text-[9px] text-gray-400 flex items-center gap-0.5 flex-shrink-0">
                        <Building2 size={9} /> {t.branch_name}
                      </span>
                    )}
                    {assignedShift && (
                      <span className="text-[8px] px-1.5 py-0.5 rounded font-bold bg-green-50 text-green-700 flex-shrink-0">
                        {assignedShift.start_time?.slice(0,5)}-{assignedShift.end_time?.slice(0,5)}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right: Shift assignment form + existing shifts */}
        <div className="w-full lg:w-[380px] space-y-4">
          {/* Assignment form */}
          <div className="bg-white rounded-2xl shadow-sm p-5" style={{ border: "1.5px solid #D3DBE4" }}>
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays size={16} className="text-[#0A3747]" />
              <span className="text-sm font-bold text-[#0A3747]">Asignar Turno</span>
            </div>

            {/* Date */}
            <label className="block mb-3">
              <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Fecha</span>
              <input
                type="date"
                value={shiftDate}
                onChange={(e) => setShiftDate(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-sky-200 outline-none"
                data-testid="turnos-date"
              />
            </label>

            {/* Preset buttons */}
            <div className="mb-3">
              <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500 block mb-1.5">Turno Predefinido</span>
              <div className="flex gap-2">
                {Object.entries(PRESETS).map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => handlePresetChange(key)}
                    className="flex-1 px-3 py-2 rounded-xl text-xs font-bold transition border"
                    style={{
                      background: shiftType === key ? p.color : "transparent",
                      color: shiftType === key ? "#fff" : p.color,
                      borderColor: p.color,
                    }}
                    data-testid={`turnos-preset-${key}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom time */}
            <div className="flex gap-3 mb-3">
              <label className="flex-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Hora Inicio</span>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => { setStartTime(e.target.value); setShiftType("custom"); }}
                  className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-sky-200 outline-none font-mono"
                  data-testid="turnos-start-time"
                />
              </label>
              <label className="flex-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Hora Fin</span>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => { setEndTime(e.target.value); setShiftType("custom"); }}
                  className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-sky-200 outline-none font-mono"
                  data-testid="turnos-end-time"
                />
              </label>
            </div>

            {/* Notes */}
            <label className="block mb-4">
              <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Notas (opcional)</span>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Guardia extendida, cobertura especial..."
                className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2 text-[11px] focus:ring-2 focus:ring-sky-200 outline-none"
                data-testid="turnos-notes"
              />
            </label>

            {/* Assign button */}
            <button
              onClick={handleAssign}
              disabled={saving || selected.size === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-40"
              style={{ background: selected.size > 0 ? "#0A3747" : "#94A3B8" }}
              data-testid="turnos-assign-btn"
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Plus size={16} />
              )}
              {saving ? "Asignando..." : `Asignar a ${selected.size} tecnico${selected.size !== 1 ? "s" : ""}`}
            </button>
          </div>

          {/* Existing shifts for the date */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ border: "1.5px solid #D3DBE4" }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <Clock size={14} className="text-[#0A3747]" />
              <span className="text-xs font-bold text-[#0A3747]">Turnos del {shiftDate}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-lg font-mono font-bold bg-sky-50 text-sky-700">
                {shifts.length}
              </span>
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              {shiftsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={16} className="animate-spin text-gray-400" />
                </div>
              ) : shifts.length === 0 ? (
                <div className="text-center text-[11px] text-gray-400 py-8">Sin turnos asignados</div>
              ) : (
                shifts.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center gap-2 px-4 py-2 border-b border-gray-50 hover:bg-gray-50/50"
                    data-testid={`shift-row-${s.id}`}
                  >
                    <div
                      className="w-1.5 h-8 rounded-full flex-shrink-0"
                      style={{ background: PRESETS[s.shift_type]?.color || "#64748B" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold text-[#0A3747] truncate">
                        {s.tech_display_name || s.technician_name || s.technician_id}
                      </div>
                      <div className="text-[9px] text-gray-400">
                        {s.start_time?.slice(0,5)} - {s.end_time?.slice(0,5)}
                        <span className="ml-1.5 font-semibold" style={{ color: PRESETS[s.shift_type]?.color || "#64748B" }}>
                          {PRESETS[s.shift_type]?.name || "Custom"}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteShift(s.id)}
                      className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 transition"
                      data-testid={`shift-delete-${s.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
